import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import { useUserPortal } from '../hooks/useUserPortal';

/**
 * Avatar / profile menu shown in the header. When signed-out, renders a "Sign in" button.
 * When signed-in, renders the avatar bubble with the first letter of the display name and a
 * dropdown with Settings and Sign out.
 */
export function ProfileMenu({ onNavigate }: { onNavigate: (route: string) => void }) {
  const { account, isSignedIn, signIn, signOut } = useAuth();
  const { hasPortalAccess } = useUserPortal();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (!isSignedIn) {
    return (
      <button onClick={signIn} className="hover-header-btn" style={styles.primary}>
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 21 21" style={{ marginRight: 8, verticalAlign: 'middle', flexShrink: 0 }}>
          <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
          <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
          <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
        </svg>
        Sign in with Microsoft
      </button>
    );
  }

  const label = (account?.username ?? account?.name ?? '?').trim();
  const initial = label.charAt(0).toUpperCase();

  return (
    <div ref={ref} style={styles.container}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="hover-avatar"
        style={styles.avatar}
        aria-haspopup="menu"
        aria-expanded={open}
        title={label}
      >
        {initial}
      </button>
      {open && (
        <div role="menu" style={styles.menu}>
          <div style={styles.menuHeader}>{label}</div>
          {hasPortalAccess && (
            <button
              role="menuitem"
              onClick={() => {
                setOpen(false);
                window.location.hash = '#/admin';
              }}
              style={styles.menuItem}
            >
              Admin portal
            </button>
          )}
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onNavigate('settings');
            }}
            style={styles.menuItem}
          >
            Settings
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
            style={styles.menuItem}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { position: 'relative' },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: '50%',
    background: '#0078d4',
    color: 'white',
    border: 'none',
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  menu: {
    position: 'absolute',
    right: 0,
    top: 'calc(100% + 6px)',
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
    boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
    minWidth: 200,
    padding: 4,
    zIndex: 100,
  },
  menuHeader: {
    padding: '8px 12px',
    fontSize: 12,
    color: '#6b7280',
    borderBottom: '1px solid #f3f4f6',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  menuItem: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    padding: '8px 12px',
    fontSize: 14,
    cursor: 'pointer',
    color: '#111827',
  },
  primary: {
    background: 'transparent',
    color: 'white',
    border: '1.5px solid white',
    padding: '10px 24px',
    borderRadius: 999,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 14,
    display: 'inline-flex',
    alignItems: 'center',
    whiteSpace: 'nowrap',
  },
};
